import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Badge, Burger, Button, Card, Menu, Stack, Switch, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, TrashIcon } from "@phosphor-icons/react";
import { IconClipboardList, IconSteeringWheel, IconTruck, IconTruckDelivery } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { parseAsJson, useQueryState } from "nuqs";
import { ChangeEvent, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import z from "zod";

import { Fields } from "@/components/_Fields";
import { Input } from "@/components/_Input";
import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSession } from "@/hooks";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { DriverGroupArgsSchema, /* QueueStatus, */ useListDriversGroupInfiniteQuery } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_base/conjuntos/")({
  component: RouteComponent
});

enum FilterType {
  IN_QUEUE = "inQueue",
  WITHOUT_VEHICLE = "withoutVehicle"
}

type GroupArgs = z.infer<typeof GroupArgsSchema>;
const GroupArgsSchema = DriverGroupArgsSchema.omit({ from_id: true, page_size: true });

function RouteComponent() {
  return (
    <Stack>
      <div className="flex items-center justify-between gap-[inherit]">
        <DashboardComponents.Title>Gestão de Conjuntos</DashboardComponents.Title>

        <MenuLinks />
      </div>

      <Content />
    </Stack>
  );
}

function Content() {
  const navigate = useNavigate();
  const [query, setQuery] = useQueryState("conjuntos", parseAsJson(GroupArgsSchema.extend({ inQueue: z.boolean().optional(), withoutVehicle: z.boolean().optional() })));

  const { company } = useSession();
  const [filters, setFilters] = useState({
    inQueue: false,
    withoutVehicle: false
  });

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage } = useListDriversGroupInfiniteQuery({
    from_id: String(company?.id),
    ...query
  });
  const infinityData = data?.pages.flatMap((page) => page.data) || [];

  const {
    formState: { isDirty },
    handleSubmit,
    setValue,
    control
  } = useForm({
    mode: "all",
    resolver: zodResolver(GroupArgsSchema),
    disabled: isFetching,
    defaultValues: {
      ...query
    }
  });

  const onSubmit: SubmitHandler<GroupArgs> = (data) => {
    setQuery(data);
  };

  const handleDelete = ({ type, id }: { type: "driver" | "truck"; id: number }) => {
    const matcher: Record<typeof type, (id: number) => void> = Object.freeze({
      driver: (id) => MODAL_HANDLER.delete.driver({ id }),
      truck: (id) => MODAL_HANDLER.delete.truck({ id })
    });

    if (!(type in matcher)) return toast.error("Tipo inválido para exclusão.");

    matcher[type](id);
  };

  const handleFilters =
    ({ type }: { type: FilterType }) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.currentTarget;

      const matcher: Record<FilterType, () => void> = Object.freeze({
        [FilterType.IN_QUEUE]: () => setFilters((prev) => ({ ...prev, [type]: checked })),
        [FilterType.WITHOUT_VEHICLE]: () => setFilters((prev) => ({ ...prev, [type]: checked }))
      });
      const matcherkeys = Object.keys(matcher);

      if (!(type in matcher)) return console.error(`O tipo ${type} definido não existe, apenas ${matcherkeys.join(", ")}`);

      matcher[type]();
    };

  const handleClear = (key: keyof GroupArgs) => () => {
    setValue(key, "");
  };

  const rows = useMemo(() => {
    const { inQueue, withoutVehicle } = filters;

    if (inQueue) {
      return infinityData.filter((driver) => driver.queue.length > 0) /* .filter((driver) => driver.truck.find((truck) => truck.id === driver.truckId)) */;
    }

    if (withoutVehicle) {
      return infinityData.filter((driver) => !driver.truck[0]?.licensePlate);
    }

    return infinityData;
  }, [data, filters]);

  const columns: GridColDef<(typeof infinityData)[number]>[] = [
    { field: "id", headerName: "ID", minWidth: 90, type: "number" },
    {
      field: "queue",
      display: "flex",
      headerName: "Fila",
      renderCell: ({ row }) => {
        const { queue } = row;
        const innerQueue = queue[0];

        return (
          <>
            {filters.inQueue && innerQueue && row.queueStatus !== null && (
              <div className="flex flex-col">
                <span>{dayjs(innerQueue.date).format("DD/MM")}</span>
                <time>{dayjs(innerQueue.date).format("hh:mm")}</time>
              </div>
            )}
          </>
        );
      }
    },
    {
      field: "name",
      headerName: "Motorista",
      minWidth: 400,
      flex: 1,
      type: "string",
      display: "flex",
      renderCell: ({ row, value }) => {
        const { id, isEnabled } = row;

        return (
          <div className="flex flex-col">
            <div className="flex gap-2">
              <Text fw="bold" className="flex gap-1">
                ID: <span className="font-normal">{id}</span>
              </Text>
              {isEnabled ? <Badge color="green">Ativo</Badge> : <Badge color="red"> Inativo</Badge>}
            </div>

            <Text>{value}</Text>
          </div>
        );
      }
    },
    {
      field: "cpf",
      headerName: "Cpf | Nascimento",
      minWidth: 200,
      type: "string",
      display: "flex",
      renderCell: ({ row, value }) => {
        const { birthDate } = row;
        const formattedBirthDate = dayjs(birthDate, "YYYY-MM-DD").format("DD/MM/YYYY");

        return (
          <div className="flex flex-col">
            <Text>{value}</Text>
            <Text>{formattedBirthDate}</Text>
          </div>
        );
      }
    },
    {
      field: "driverActions",
      headerName: "Ações do Motorista",
      type: "actions",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { id } = row;

        return (
          <ActionIcon.Group>
            <ActionIcon
              variant="light"
              color="yellow"
              onClick={() => navigate({ to: "/dashboard/motoristas/editar/$id/modal", params: { id }, state: { redirectTo: "/dashboard/conjuntos" } })}
            >
              <PencilIcon />
            </ActionIcon>

            <ActionIcon variant="light" color="red" onClick={() => handleDelete({ type: "driver", id })}>
              <TrashIcon />
            </ActionIcon>

            {/* <ActionIcon variant="light" color="green">
                <IconLogin2 size={20} />
              </ActionIcon> */}

            <ActionIcon color="blue" variant="light" onClick={() => navigate({ to: "/dashboard/logs/$id/modal", params: { id }, state: { redirectTo: "/dashboard/conjuntos" } })}>
              <IconClipboardList size={20} stroke={1.5} />
            </ActionIcon>
          </ActionIcon.Group>
        );
      }
    },
    {
      field: "truck",
      headerName: "Veículo | Marca - Modelo",
      valueGetter: (val, { truck }) => truck[0]?.licensePlate || "N/A",
      type: "string",
      minWidth: 200,
      display: "flex",
      flex: 1,
      renderCell: ({ row, value }) => {
        const { truck } = row;
        const truckInfo = truck[0];
        const modelInfoName = truckInfo?.vehicleModel.name || "N/A";
        const brandInfoName = truckInfo?.vehicleModel.truckBrand.name || "N/A";

        return (
          <div className="flex flex-col">
            <Text>{value}</Text>
            <Text>
              {brandInfoName} - {modelInfoName}
            </Text>
          </div>
        );
      }
    },
    {
      field: "truckActions",
      headerName: "Ações do Caminhão",
      type: "actions",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { truck } = row;
        const currentTruck = truck[0];
        const id = currentTruck?.id || 0;
        const plate = currentTruck?.licensePlate;

        if (!plate) return;

        return (
          <ActionIcon.Group>
            <ActionIcon
              variant="light"
              color="yellow"
              onClick={() => navigate({ to: "/dashboard/caminhoes/editar/$id/modal", params: { id }, state: { redirectTo: "/dashboard/conjuntos" } })}
            >
              <PencilIcon />
            </ActionIcon>

            <ActionIcon variant="light" color="red" onClick={() => handleDelete({ type: "truck", id })}>
              <TrashIcon />
            </ActionIcon>
          </ActionIcon.Group>
        );
      }
    },
    {
      field: "shippingCompany",
      headerName: "Transportadora",
      valueGetter: (val, { shippingCompany }) => shippingCompany?.name || "N/A",
      type: "string",
      minWidth: 200,
      flex: 1
    },
    {
      field: "company",
      headerName: "Empresa",
      valueGetter: (val, { company }) => company?.name || "N/A",
      type: "string",
      minWidth: 200,
      flex: 1
    }
  ];

  return (
    <>
      <Title order={2}>Filtros</Title>

      <Card withBorder>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
              <Input.Text control={control} name="driver_search" label="Motorista" placeholder="Nome ou CPF" />
              <Input.Text control={control} name="truck_search" label="Caminhão" placeholder="Placa do veículo" />

              <Fields.Companies control={control} name="companyId" onClear={handleClear("companyId")} />
              <Fields.ShippingCompanies control={control} name="shippingCompanyId" onClear={handleClear("shippingCompanyId")} />
            </div>

            <div className="flex flex-col items-start">
              <Text>Status</Text>
              <Input.SegmentedControl
                control={control}
                name="status"
                data={[
                  {
                    label: "Nenhum",
                    value: "Nenhum"
                  },
                  {
                    label: "Ativo",
                    value: "1"
                  },
                  {
                    label: "Inativo",
                    value: "0"
                  }
                ]}
              />
            </div>

            <div className="flex flex-col gap-[inherit] md:flex-row">
              <Switch label="Na Fila" color="teal" size="md" disabled={filters.withoutVehicle} checked={filters.inQueue} onChange={handleFilters({ type: FilterType.IN_QUEUE })} />
              <Switch
                label="Sem Veículo"
                color="teal"
                size="md"
                disabled={filters.inQueue}
                checked={filters.withoutVehicle}
                onChange={handleFilters({ type: FilterType.WITHOUT_VEHICLE })}
              />
            </div>

            <Button type="submit" disabled={!isDirty} loading={isLoading} className="self-end">
              Aplicar Filtros
            </Button>
          </Stack>
        </form>
      </Card>

      <Stack>
        <Button onClick={fetchNextPage} loading={isFetchingNextPage} disabled={!hasNextPage} className="self-end">
          Carregar Mais
        </Button>

        <DataGridBase
          rows={rows}
          loading={isFetching}
          columns={columns}
          columnVisibilityModel={{
            id: false,
            queue: filters.inQueue
          }}
        />
      </Stack>
    </>
  );
}

function MenuLinks() {
  const navigate = useNavigate({ from: "/dashboard/conjuntos" });
  const [opened, { toggle }] = useDisclosure();

  return (
    <Menu opened={opened} position="bottom-end">
      <Menu.Target>
        <Burger opened={opened} onClick={toggle} />
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          onClick={() => navigate({ to: "/dashboard/motoristas/cadastrar/modal", state: { redirectTo: "/dashboard/conjuntos" } })}
          leftSection={<IconSteeringWheel size={14} />}
        >
          Cadastrar Motorista
        </Menu.Item>

        <Menu.Item onClick={() => navigate({ to: "/dashboard/caminhoes/cadastrar/modal", state: { redirectTo: "/dashboard/conjuntos" } })} leftSection={<IconTruck size={14} />}>
          Cadastrar Caminhão
        </Menu.Item>

        <Menu.Item
          onClick={() => navigate({ to: "/dashboard/conjuntos/vincular/transportadora", state: { redirectTo: "/dashboard/conjuntos" } })}
          leftSection={<IconTruckDelivery size={14} />}
        >
          Vincular Transportadora
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
